
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Catalog, CatalogPage } from "@/types/catalog";

export function useCatalogs() {
  return useQuery({
    queryKey: ["catalogs", "v2"], // Added version to force cache invalidation
    queryFn: async () => {
      const { data, error } = await supabase
        .from("catalogs")
        .select("*")
        .order("title", { ascending: false });

      if (error) throw error;
      return data as Catalog[];
    },
  });
}

export function useCatalogPages(catalogId: string) {
  return useQuery({
    queryKey: ["catalog-pages", catalogId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("catalog_pages")
        .select("*")
        .eq("catalog_id", catalogId)
        .order("page_number");

      if (error) throw error;
      return data as CatalogPage[];
    },
  });
}

export function useUploadCatalogPage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      catalogId, 
      pageNumber, 
      pageTitle, 
      styleNumbers,
      file 
    }: { 
      catalogId: string; 
      pageNumber: number; 
      pageTitle?: string;
      styleNumbers: number[];
      file: File;
    }) => {
      // Upload image to storage
      const fileExt = file.name.split('.').pop();
      const filePath = `${catalogId}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError, data } = await supabase.storage
        .from('catalog-pages')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get the public URL for the uploaded file
      const { data: { publicUrl } } = supabase.storage
        .from('catalog-pages')
        .getPublicUrl(filePath);

      // Create the catalog page record
      const { error: insertError } = await supabase
        .from('catalog_pages')
        .insert({
          catalog_id: catalogId,
          page_number: pageNumber,
          page_title: pageTitle,
          style_numbers: styleNumbers,
          image_url: publicUrl,
          thumbnail_url: publicUrl,
        });

      if (insertError) throw insertError;

      return { success: true };
    },
    onSuccess: (_, { catalogId }) => {
      queryClient.invalidateQueries({ queryKey: ["catalog-pages", catalogId] });
    },
  });
}

export function useBagCatalogs(styleNumber: string) {
  return useQuery({
    queryKey: ["bag-catalogs", styleNumber],
    queryFn: async () => {
      const styleNum = parseInt(styleNumber);
      if (isNaN(styleNum)) {
        console.error('Invalid style number:', styleNumber);
        return [];
      }
      
      console.log('Querying catalog pages for style number:', styleNum);
      
      const { data, error } = await supabase
        .from("catalog_pages")
        .select(`
          id,
          page_number,
          style_numbers,
          thumbnail_url,
          catalogs (
            id,
            title
          )
        `)
        .contains('style_numbers', [styleNum]);

      if (error) {
        console.error('Error fetching catalog pages:', error);
        throw error;
      }

      console.log('Found catalog pages:', data);
      return data;
    },
  });
}

export function useUncatalogedBags() {
  return useQuery({
    queryKey: ["uncataloged-bags"],
    queryFn: async () => {
      console.log('Fetching bags without catalog entries...');
      
      const { data: bagsWithoutCatalogs, error } = await supabase
        .from('coach_bag_real_data')
        .select('style_number, Name')
        .not('style_number', 'is', null)
        .order('style_number');

      if (error) {
        console.error('Error fetching uncataloged bags:', error);
        throw error;
      }

      if (!bagsWithoutCatalogs?.length) {
        console.log('No bags found');
        return [];
      }

      const { data: catalogPages } = await supabase
        .from('catalog_pages')
        .select('style_numbers');

      const catalogedStyles = new Set(
        catalogPages?.flatMap(page => page.style_numbers) || []
      );

      const uncatalogedBags = bagsWithoutCatalogs.filter(
        bag => !catalogedStyles.has(Number(bag.style_number))
      );

      console.log('Found uncataloged bags:', uncatalogedBags);
      return uncatalogedBags;
    },
  });
}
